// Pure candidate selector for cross-device (Path B) recovery. Given the server
// pending list + injected device/dismiss/give-up/active/recovered state, returns
// the recordings THIS device should try to pull-and-recover, newest first.
//
// Path B only touches OTHER-device WEB recordings: a this-device recording is
// owned by the local drain / Path A (the M7 "defer to local" rule), so the two
// paths never race on the same recording — and even if a recording somehow synced
// to two devices, reuse-id + server M7 dedup collapses it to one transcript.
//
// Staleness rule: never act on a recording that is still actively streaming on the
// other device. Never gates on percent (segmentsReceived is a high-water mark — a
// lost middle segment still reads ~100%). This used to be phrased as "mirrors
// RecordingRecoveryBar's legacy nudge"; that nudge was removed 2026-07-30 and the
// silent recovery path is now the only consumer of these thresholds.
export const STALE_MS = 90000; // ~9 segment intervals — a segmented recording gone quiet
export const NO_ACTIVITY_STALE_MS = 10 * 60 * 1000; // a recording that never streamed a segment

// UPPER bound on how long a stuck recording keeps nagging (2026-07-15). The
// staleness thresholds above are the LOWER bound (don't nag a still-live
// recording); this is when to STOP. Past a day of inactivity the banner is
// noise, not a useful prompt — it re-surfaces for days, and on every fresh
// browser profile / incognito session (each mints a new deviceId, so the same
// old recording looks "stuck on another device" again). Applies to WEB
// recordings only: a stuck web recording's audio is retained server-side 14 days
// (last-copy protection) and stays recoverable, so silencing the repeat nag
// loses nothing. A NATIVE recording's audio lives only on the phone (no server
// copy), so callers must NOT quiet its "open your phone to finish it" reminder —
// see the origin check at each call site. Decoupling the nag lifetime from the
// retention lifetime is the whole point.
//
// NO PRODUCTION CONSUMER as of 2026-07-30. Its only two callers were the recovery
// banner's poll filter and the nudge's quiet bound, both removed with the banner
// itself; only this module's own tests import it now. Kept (not deleted) because it
// encodes the reasoning above and is what a re-introduced prompt would need — but do
// not read its presence as evidence that anything is being quieted today.
export const NAG_MAX_AGE_MS = 24 * 60 * 60 * 1000;

// Hard ceiling on the RECOVERY enumeration (distinct from NAG_MAX_AGE_MS, which
// only quiets the visible banner). Mirrors lib/sweepPolicy.js HARD_CAP_MS on the
// server: past 14 days of inactivity the sweep has purged the archive segments, so
// there is provably nothing left to pull and listing the recording is pure load.
// This does NOT weaken the "a complete recording auto-recovers at ANY age" rule
// below — it bounds enumeration at the exact point the bytes stop existing. Keep
// the two constants in sync if the server's cap moves.
export const RECOVERY_MAX_AGE_MS = 14 * 24 * 60 * 60 * 1000;

// Upper bound on how many recordings one poll will enumerate. The selector is
// newest-first, so the remainder rolls into later polls rather than being dropped.
// Without this a user's whole stale backlog became one serial burst of
// Recording - Get Segments calls, all landing inside the same second.
export const MAX_CANDIDATES_PER_SWEEP = 10;

// Timestamp of a pending row's most recent activity: a segmented recording that
// streamed then died is measured from its last segment; one that never streamed,
// from its start. 0 when the row carries neither (a malformed row) — callers that
// must not vanish a live prompt should treat 0 as "unknown, keep showing".
export function lastActivityAt(r) {
  return Math.max(r.lastSegmentAt || 0, r.startedAt || 0);
}

// Age since that activity, compared against NAG_MAX_AGE_MS so a long recording
// that only just went quiet is still nagged while an old one is quieted.
export function inactivityAge(r, now) {
  return now - lastActivityAt(r);
}

// A begin racing a Store Segment leaves TWO manifest rows for one recordingId
// server-side ("observed in prod daily" per the manifest lib), and the pending
// list returns both. Collapse to one row per id, preferring the row that actually
// identifies the owning device (the orphan twin has null deviceId/deviceLabel),
// then the freshest segment activity — otherwise the twin that wins the sort is
// luck, the banner can show a null device label, and the poll would run the whole
// pull twice for one recording.
export function dedupPendingRows(pending) {
  const byId = new Map();
  for (const r of pending || []) {
    if (!r || !r.recordingId) continue;
    const prev = byId.get(r.recordingId);
    if (!prev) {
      byId.set(r.recordingId, r);
      continue;
    }
    let keep = prev;
    let other = r;
    if (!!r.deviceId !== !!prev.deviceId) {
      if (r.deviceId) {
        keep = r;
        other = prev;
      }
    } else if ((r.lastSegmentAt || 0) > (prev.lastSegmentAt || 0)) {
      keep = r;
      other = prev;
    }
    // MERGE the progress counters, don't just keep one row's: segment bumps can
    // land on the orphan twin (recordSegment updates whichever row it finds), so
    // the deviceId row can read segmentsReceived 0 while the twin holds the real
    // progress — and the banner's evidence gate would misread a genuinely stuck
    // recording as debris.
    byId.set(r.recordingId, {
      ...keep,
      segmentsReceived: Math.max(keep.segmentsReceived || 0, other.segmentsReceived || 0),
      lastSegmentAt: Math.max(keep.lastSegmentAt || 0, other.lastSegmentAt || 0),
    });
  }
  return [...byId.values()];
}

// What the PENDING-LIST row alone can tell us about a recording's completeness,
// without spending a Recording - Get Segments call on it. The backend stamps
// presentIndicesAll / segmentsExpectedAll (unioned across duplicate manifest rows)
// onto every row for that recordingId, so this is the same answer Get Segments
// would give — for the gate. It is NOT a substitute for the call on the recovery
// path, which needs segment fileNames to fetch bytes.
//
// "unknown" is the safe default and covers an older backend that does not send the
// fields at all, plus any payload the api-layer normalizer refused to trust. The
// caller must ASK the server on unknown — never assume a state.
export const PENDING_GATE = {
  UNKNOWN: "unknown",
  COMPLETE: "complete",
  // Server holds no fetchable bytes for this recording right now. This is the
  // signal that feeds the empty-read strike budget, which is why the normalizer
  // must never coerce a mangled payload into an empty array.
  EMPTY: "incomplete-empty",
  // Some bytes present, but not provably contiguous through the final segment.
  PARTIAL: "incomplete-partial",
};

export function pendingRowGate(r, isComplete) {
  if (!r) return PENDING_GATE.UNKNOWN;
  const indices = r.presentIndicesAll;
  const expected = r.segmentsExpectedAll;
  if (!Array.isArray(indices) || typeof expected !== "number") return PENDING_GATE.UNKNOWN;
  if (typeof isComplete === "function" && isComplete({ segmentsExpected: expected, presentIndices: indices })) {
    return PENDING_GATE.COMPLETE;
  }
  return indices.length === 0 ? PENDING_GATE.EMPTY : PENDING_GATE.PARTIAL;
}

export function selectCrossDeviceCandidates(
  pending,
  {
    myDevice,
    myUserId,
    now,
    isDismissed,
    activeRecordingId,
    giveUpOf,
    maxGiveUp = 3,
    isNonIdempotent,
    inFlight,
    wasRecovered,
    isEmptySkipped,
    maxPerSweep = MAX_CANDIDATES_PER_SWEEP,
  } = {}
) {
  const out = dedupPendingRows(pending).filter((r) => {
    if (r.origin !== "web") return false; // only web recordings have server segments to pull
    if (r.deviceId && r.deviceId === myDevice) return false; // this device → local drain / Path A owns it
    // Owner scoping — mirrors the local-drain guard at useIndexDb.js:176
    // (`r?.userId && r.userId !== currentUserId`). On a shared clinic
    // workstation, a foreign row must never surface as a candidate.
    // FAIL OPEN both ways: a row with no `userId` is NOT dropped (the
    // backend may not surface it yet — Recording - Pending List is not
    // confirmed to filter/stamp ownership), and when `myUserId` itself is
    // not provided, nothing is dropped on ownership grounds (unchanged
    // behavior for any caller that hasn't threaded it through yet).
    if (myUserId && r.userId && r.userId !== myUserId) return false;
    if (activeRecordingId && r.recordingId === activeRecordingId) return false; // live somewhere
    if (typeof isDismissed === "function" && isDismissed(r.recordingId)) return false;
    // Already recovered here: the transcript save is async, so the server row
    // stays pending until STT finishes (minutes). Without this the poll re-pulls
    // all the audio and re-uploads every 60s until the manifest closes.
    if (typeof wasRecovered === "function" && wasRecovered(r.recordingId)) return false;
    if (typeof isNonIdempotent === "function" && isNonIdempotent(r.chatId, r.phase)) return false; // Quick Start / AI Update Note
    if (typeof giveUpOf === "function" && giveUpOf(r.recordingId) >= maxGiveUp) return false; // repeatedly failed
    if (inFlight && typeof inFlight.has === "function" && inFlight.has(r.recordingId)) return false; // single-flight
    // Repeatedly returned no server segments THIS SESSION (see EMPTY_GIVEUP_ROUNDS).
    // Must be filtered HERE rather than skipped inside the caller's loop: maxPerSweep
    // slices below, so an in-loop skip would let a handful of dead recordings occupy
    // every slot forever and starve the rest of the backlog.
    if (typeof isEmptySkipped === "function" && isEmptySkipped(r.recordingId)) return false;
    // The ~1-day quiet bound (NAG_MAX_AGE_MS) is deliberately NOT applied here: a
    // provably-complete recording must keep silently auto-recovering into the note at
    // ANY age within the server's retention (that path shows nothing, so it is never
    // the noise we're quieting). It used to be applied to the VISIBLE surfaces — the
    // legacy bar filter and the nudge display in RecordingRecoveryBar — and those
    // were removed 2026-07-30, so it is now applied nowhere in production.
    //
    // RECOVERY_MAX_AGE_MS is a different thing and DOES apply: past the server's own
    // 14-day hard cap the segments have been swept, so there is nothing to recover
    // and enumerating the row is pure load. Previously unbounded, which let a
    // permanently-unrecoverable backlog grow without limit.
    if (inactivityAge(r, now) > RECOVERY_MAX_AGE_MS) return false;
    // Staleness: only a recording that has gone quiet on the other device.
    if (r.lastSegmentAt > 0) return now - r.lastSegmentAt > STALE_MS;
    return now - (r.startedAt || 0) > NO_ACTIVITY_STALE_MS;
  });
  out.sort((a, b) => (b.startedAt || 0) - (a.startedAt || 0));
  // Newest-first slice, so a large backlog is worked through across successive polls
  // instead of fanning out into one same-second burst of per-recording calls.
  return maxPerSweep > 0 ? out.slice(0, maxPerSweep) : out;
}
